/**
 * Create card: name + member multi-select. Creating is side-effect free
 * (a named roster), so no confirm; the toast points at the assign step.
 */
import { useMemo, useState } from 'react';
import { GatedButton, ReasonNote } from '../GatedButton';
import { MemberPicker } from './MemberPicker';

export function CreateCohortCard({
  userEmails,
  busy,
  onCreate,
}: {
  userEmails: string[];
  busy: boolean;
  onCreate: (name: string, memberEmails: string[]) => Promise<boolean>;
}) {
  const [name, setName] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [query, setQuery] = useState('');
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return q ? userEmails.filter((e) => e.toLowerCase().includes(q)) : userEmails;
  }, [userEmails, query]);

  async function submit() {
    const ok = await onCreate(name.trim(), Array.from(selected));
    if (ok) {
      setName('');
      setSelected(new Set());
    }
  }

  const nameReason = name.trim() === '' ? 'Name the cohort first' : null;

  return (
    <div className="card">
      <div className="card-head">
        <div>
          <h3>New cohort</h3>
          <div className="sub">
            A named roster, reusable across orientations — e.g. “Truck
            drivers 2026-09”. Members can be added or removed later.
          </div>
        </div>
      </div>
      <div className="card-body" style={{ display: 'grid', gap: 8 }}>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <input
            type="text"
            className="inp"
            style={{ flex: 1, minWidth: 220 }}
            placeholder='Cohort name, e.g. "Truck drivers 2026-09"'
            maxLength={120}
            value={name}
            onChange={(e) => setName(e.target.value)}
            aria-label="Cohort name"
          />
          <input
            type="search"
            className="inp"
            style={{ flex: 1, minWidth: 180 }}
            placeholder="Filter members…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            aria-label="Filter members"
          />
          <GatedButton
            className="btn primary"
            busy={busy}
            reason={nameReason}
            onClick={() => void submit()}
          >
            {busy
              ? 'Creating…'
              : selected.size > 0
                ? `Create with ${selected.size} member${selected.size === 1 ? '' : 's'}`
                : 'Create empty'}
          </GatedButton>
        </div>
        {/* Inline too once members are ticked: that is when the dimmed
            Create button is the puzzle (#262). */}
        {selected.size > 0 ? <ReasonNote reason={nameReason} /> : null}
        <MemberPicker
          emails={filtered}
          selected={selected}
          onToggle={(email) =>
            setSelected((prev) => {
              const next = new Set(prev);
              if (next.has(email)) next.delete(email);
              else next.add(email);
              return next;
            })
          }
        />
      </div>
    </div>
  );
}
